import { StrictMode, Suspense, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/shadcn/tooltip'
import { Toaster } from '@/components/shadcn/sonner'
import { AppLayout } from '@/components/layout/AppLayout'
import { LandingRoute } from '@/components/layout/LandingRoute'
import ProtectedRoute from '@/components/ProtectedRoute'
import { useAuthStore } from '@/store/auth.store'
import { RequirePermission } from '@/components/permission/RequirePermission'
import type { PermissionCheck } from '@/components/permission/permission-check'
import { PermissionEntityType } from '@/api/model'
import LoginPage from '@/pages/login/LoginPage'
import ForgotPasswordPage from '@/pages/login/ForgotPasswordPage'
import RegisterPage from '@/pages/login/RegisterPage'
import ResetPasswordPage from '@/pages/login/ResetPasswordPage'
import EmailConfirmationPage from '@/pages/login/EmailConfirmationPage'
import EmployeesPage from '@/pages/employees/EmployeesPage'
import EmployeeDetailsPage from '@/pages/employees/EmployeeDetailsPage'
import AuditLogsPage from '@/pages/audit-logs/AuditLogsPage'
import ServiceConnectionsPage from '@/pages/service-connections/ServiceConnectionsPage'
import AppDesignPage from '@/pages/configuration/AppDesignPage'
import GroupsPage from '@/pages/groups/GroupsPage'
import WalletPage from '@/pages/wallet/WalletPage'
import WalletTypesPage from '@/pages/wallet-types/WalletTypesPage'
import BillingPage from '@/pages/billing/BillingPage'
import CompanyPage from '@/pages/company/CompanyPage'
import NotificationsPage from '@/pages/notifications/NotificationsPage'
import PermissionsPage from '@/pages/permissions/PermissionsPage'
import ServiceModulesPage from '@/pages/service-modules/ServiceModulesPage'
import ActionModulesPage from '@/pages/action-modules/ActionModulesPage'
import ShopModulesPage from '@/pages/shop-modules/ShopModulesPage'
import ActionModuleDetailPage from '@/pages/action-modules/detail/ActionModuleDetailPage'
import IncentivationTab from '@/pages/action-modules/detail/incentivation/IncentivationTab'
import ImportExportTab from '@/pages/action-modules/detail/import-export/ImportExportTab'
import ModuleSettingsTab from '@/pages/action-modules/detail/settings/ModuleSettingsTab'
import ShopModulePayoutsPage from '@/pages/shop-modules/full-payout/ShopModulePayoutsPage'
import SuperAdminCompaniesPage from '@/pages/super-admin/companies/SuperAdminCompaniesPage'
import SuperAdminBillingPage from '@/pages/super-admin/billing/SuperAdminBillingPage'
import SuperAdminPermissionsPage from '@/pages/super-admin/permissions/SuperAdminPermissionsPage'
import ActionModuleEditorPage from '@/pages/action-modules/ActionModuleEditorPage'
import { RouteErrorFallback } from '@/components/common/RouteErrorFallback'
import { initSentry } from '@/lib/sentry'
import './index.css'
import './i18n'
import '@/lib/dayjs' // register dayjs plugins (utc, customParseFormat) on the global singleton
import { ThemeProvider } from 'next-themes'

initSentry()

const P = PermissionEntityType

/** Wrap a route element in a permission guard (passes if any check passes). */
function guard(element: ReactNode, ...anyOf: PermissionCheck[]): ReactNode {
  return <RequirePermission anyOf={anyOf}>{element}</RequirePermission>
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/reset_password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/linking/password_reset',
    element: <ResetPasswordPage />,
  },
  {
    path: '/linking/email_confirmation',
    element: <EmailConfirmationPage />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            // Pathless layer: catches render errors of any page below and shows
            // RouteErrorFallback inside the layout's Outlet — sidebar/header stay
            // usable so the user can recover by navigating away.
            errorElement: <RouteErrorFallback />,
            children: [
            {
              path: '/',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'dashboard' } },
              element: <LandingRoute />,
            },
            {
              path: '/employees',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'employees', path: '/employees' } },
              element: guard(<EmployeesPage />, { entityType: P.General, permission: 'AccountManagement' }),
            },
            {
              path: '/employees/:employeeId',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'employees', path: '/employees' } },
              element: guard(<EmployeeDetailsPage />, { entityType: P.General, permission: 'AccountManagement' }),
            },
            {
              path: '/audit-logs',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'audit-logs' } },
              element: guard(<AuditLogsPage />, { entityType: P.General, permission: 'AuditLog' }),
            },
            {
              path: '/app-design',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'configuration' } },
              element: guard(<AppDesignPage />, { entityType: P.General, permission: 'Customization' }),
            },
            {
              path: '/service-connections',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'service-connections' } },
              element: guard(<ServiceConnectionsPage />, { entityType: P.ServiceConnections, permission: 'View' }),
            },
            {
              path: '/billing',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'billing' } },
              element: guard(<BillingPage />, { entityType: P.General, permission: 'Billing' }),
            },
            {
              path: '/wallet/:walletId?',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'wallet' } },
              element: guard(<WalletPage />, { entityType: P.General, permission: 'CompanyWallet' }),
            },
            {
              path: '/wallet-types',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'wallet-types' } },
              element: guard(<WalletTypesPage />, { entityType: P.General, permission: 'CompanyWallet' }),
            },
            {
              path: '/groups',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'groups' } },
              element: guard(<GroupsPage />, { entityType: P.Groups, permission: 'View', any: true }),
            },
            {
              path: '/company',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'company' } },
              element: guard(
                <CompanyPage />,
                { entityType: P.General, permission: 'CompanyManagement' },
                { entityType: P.SubCompanies, permission: 'View', any: true },
                { entityType: P.SubCompanies, permission: 'Edit', any: true },
                { entityType: P.SubCompanies, permission: 'Create', any: true },
                { entityType: P.SubCompanies, permission: 'Delete', any: true },
              ),
            },
            {
              path: '/notifications',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'notifications' } },
              element: guard(<NotificationsPage />, { entityType: P.General, permission: 'GenericInAppNotifications' }),
            },
            {
              path: '/permissions',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'permissions' } },
              element: guard(<PermissionsPage />, { entityType: P.Permissions, permission: 'View' }),
            },
            {
              path: '/service-modules',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'service-modules', path: '/service-modules' } },
              element: guard(<ServiceModulesPage />, { entityType: P.ServiceModules, permission: 'View', any: true }),
            },
            {
              path: '/action-modules',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'action-modules' } },
              element: guard(<ActionModulesPage />, { entityType: P.ActionModules, permission: 'View', any: true }),
            },
            {
              // Pathless parent (no element → renders its Outlet): carries the
              // "Shopmodule" crumb so the detail page below inherits it as an ancestor.
              path: '/shop-modules',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'shop-modules', path: '/shop-modules' } },
              children: [
                {
                  index: true,
                  element: guard(<ShopModulesPage />, { entityType: P.ShopModules, permission: 'View', any: true }),
                },
                {
                  path: ':shopModuleId',
                  handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'shop-modules-fullpayout' } },
                  element: guard(<ShopModulePayoutsPage />, { entityType: P.ShopModules, permission: 'View', any: true }),
                },
              ],
            },
            {
              path: '/action-modules/:moduleId',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'action-modules', path: '/action-modules' } },
              element: guard(
                <ActionModuleDetailPage />,
                { entityType: P.ActionModules, permission: 'Incentivate', any: true },
              ),
              children: [
                { index: true, element: <Navigate to="incentivation" replace /> },
                {
                  path: 'incentivation',
                  element: guard(<IncentivationTab />, { entityType: P.ActionModules, permission: 'Incentivate', any: true }),
                },
                {
                  path: 'import-export',
                  element: guard(<ImportExportTab />, { entityType: P.ActionModules, permission: 'Incentivate', any: true }),
                },
                {
                  path: 'module-settings',
                  element: guard(<ModuleSettingsTab />, { entityType: P.ActionModules, permission: 'Incentivate', any: true }),
                },
              ],
            },
            {
              path: '/action-modules/edit/:actionModuleId',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'action-modules-wizard' } },
              element: guard(
                <ActionModuleEditorPage isAdmin={false} />,
                { entityType: P.ActionModules, permission: 'Edit', any: true },
              ),
            },
            {
              path: '/super-admin/companies',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'super-admin-companies' } },
              element: <SuperAdminCompaniesPage />,
            },
            {
              path: '/super-admin/billing',
              handle: { breadcrumb: { labelKey: 'superAdmin.breadcrumb', ns: 'billing' } },
              element: <SuperAdminBillingPage />,
            },
            {
              path: '/super-admin/permissions',
              handle: { breadcrumb: { labelKey: 'breadcrumb', ns: 'administrators' } },
              element: <SuperAdminPermissionsPage />,
            },
            {
              path: '*',
              element: <Navigate to="/" replace />,
            },
            ],
          },
        ],
      },
    ],
  },
])

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 5,
    },
  },
})

// Wipe the React Query cache whenever auth is cleared (explicit logout or
// session-expiry forced logout). Without this the cache survives the login
// page and a subsequent login shows the previous session's data.
useAuthStore.subscribe((state, prev) => {
  if (prev.isAuthenticated && !state.isAuthenticated) {
    queryClient.clear()
  }
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme='light' storageKey='vite-ui-theme'>
        <TooltipProvider>
          <Suspense fallback={null}>
            <RouterProvider router={router} />
          </Suspense>
          {/* Global (not inside AppLayout) so toasts also render on the auth pages.
              closeButton: error toasts stay up (duration: Infinity in notify)
              and are dismissed via the X. */}
          <Toaster position="bottom-right" richColors closeButton />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
)
